import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Stack,
    TextField,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion, faTruckField } from '@fortawesome/pro-solid-svg-icons';
import { useApplicationContext } from 'ApplicationContext';
import { settingsApi } from 'Pages/Admin/Settings/settingsApi';
import { ProjectInput } from 'apiClient/Api';
import { ConfigurationTemplateDto } from './catalogStructure';

/**
 * „Aus Vorlage starten".
 *
 * **Warum das hierher gehört.** Die Vorlagen lagen bisher nur im Stammdaten-Bereich und in der
 * Werkzeugkiste des Assistenten. Wer den Kalkulator von Hand bedient, hatte nichts davon — obwohl
 * genau dort der Satz fällt: „wir rüsten einen ELW 1 um".
 *
 * **Was sie setzt und was nicht.** Übernommen werden nur die feststehenden Positionen. Die offenen
 * Punkte werden angezeigt und bewusst nicht gesetzt: dass ein ELW 1 drei Funkanschaltungen hat,
 * ist Erfahrung — welches Gerät daran hängt, weiß nur der Kunde. Sie stehen als Merkliste da,
 * damit sie im Gespräch abgearbeitet werden.
 */
export const TemplatePickerDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    /** Übernimmt die feststehenden Positionen; die offenen Punkte kommen als Merkliste mit. */
    onApply: (project: ProjectInput, openQuestions: string[]) => void;
}> = ({ open, onClose, onApply }) => {
    const theme = useTheme();
    const context = useApplicationContext();
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const [templates, setTemplates] = useState<ConfigurationTemplateDto[] | null>(null);
    const [search, setSearch] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setError(null);
        http.get<ConfigurationTemplateDto[]>('/Calculator/templates')
            .then((r) => setTemplates(r.data))
            .catch(() => setError('Die Vorlagen konnten nicht geladen werden.'));
    }, [open, http]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return templates ?? [];
        return (templates ?? []).filter((t) =>
            t.title.toLowerCase().includes(q)
            || (t.stichworte ?? []).some((w) => w.toLowerCase().includes(q))
            || (t.description ?? '').toLowerCase().includes(q));
    }, [templates, search]);

    const apply = (t: ConfigurationTemplateDto) => {
        const project: ProjectInput = {
            projektname: t.title,
            instanzen: (t.positions ?? []).map((p) => ({
                requirementId: p.requirementId,
                menge: p.quantity,
                area: 'A',
            })),
        };
        onApply(project, (t.openPositions ?? []).map((o) => `${o.quantity}× aus ${o.groupPath}: ${o.question}`));
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Aus Vorlage starten</DialogTitle>

            <DialogContent>
                <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                    Übernommen wird, was feststeht. Was offen ist, erscheint als Merkliste — und wird
                    im Gespräch geklärt, statt geraten zu werden.
                </Alert>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <TextField
                    size="small"
                    fullWidth
                    label="Suche"
                    placeholder="ELW, DLRG, Einsatzzentrale …"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ mb: 2 }}
                />

                {templates === null && !error && <Typography variant="body2">Wird geladen …</Typography>}

                {templates !== null && filtered.length === 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Keine Vorlage passt zu dieser Suche.
                    </Typography>
                )}

                <Stack spacing={1.5}>
                    {filtered.map((t) => (
                        <Paper key={t.id} variant="outlined" sx={{ p: 1.5 }}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                                <Box sx={{ color: 'primary.main', mt: 0.25 }}>
                                    <FontAwesomeIcon icon={faTruckField} />
                                </Box>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="subtitle2">{t.title}</Typography>
                                    {t.description && (
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {t.description}
                                        </Typography>
                                    )}
                                </Box>
                                {t.anlagenart && <Chip size="small" label={t.anlagenart} />}
                                {t.isDraft && (
                                    <Chip size="small" color="warning" variant="outlined" label="Entwurf" />
                                )}
                                <Button size="small" variant="contained" onClick={() => apply(t)}>
                                    Übernehmen
                                </Button>
                            </Stack>

                            {(t.openPositions ?? []).length > 0 && (
                                <>
                                    <Divider sx={{ my: 1.25 }} />
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                        Danach zu klären
                                    </Typography>
                                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                        {t.openPositions!.map((o) => (
                                            <Stack
                                                key={`${o.groupPath}-${o.question}`}
                                                direction="row"
                                                spacing={0.75}
                                                sx={{
                                                    alignItems: 'flex-start',
                                                    p: 0.75,
                                                    borderRadius: 1,
                                                    bgcolor: alpha(theme.palette.info.main, 0.06),
                                                }}
                                            >
                                                <Box sx={{ color: 'info.main', mt: 0.2 }}>
                                                    <FontAwesomeIcon icon={faCircleQuestion} fontSize={11} />
                                                </Box>
                                                <Typography variant="caption">
                                                    <b>{o.quantity}×</b> aus {o.groupPath} — {o.question}
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </>
                            )}
                        </Paper>
                    ))}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Schließen</Button>
            </DialogActions>
        </Dialog>
    );
};

export default TemplatePickerDialog;
